import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useLocalStorage } from '@/shared/lib/react';
import { lightTheme, darkTheme } from '@/shared/ui/theme';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ShopThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  actualTheme: ResolvedTheme;
}

export const ShopThemeContext = createContext<ShopThemeContextValue | undefined>(undefined);

const PUBLIC_ROUTE_PREFIXES = ['/book', '/public'];

const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

// eslint-disable-next-line react-refresh/only-export-components
export const useShopTheme = () => {
  const context = useContext(ShopThemeContext);
  if (!context) {
    throw new Error('useShopTheme must be used within a ShopThemeProvider');
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const ShopThemeProvider = ({ children }: Props) => {
  const [theme, setTheme] = useLocalStorage<Theme>('shop-theme', 'system');
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);
  const { pathname } = useLocation();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;
  const actualTheme: ResolvedTheme = isPublicRoute ? 'light' : resolvedTheme;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(actualTheme);
  }, [actualTheme]);

  const muiTheme = actualTheme === 'dark' ? darkTheme : lightTheme;

  return (
    <ShopThemeContext.Provider value={{ theme, setTheme, actualTheme }}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ShopThemeContext.Provider>
  );
};
